import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { fetchRoadmapItems, createRoadmapItem, updateRoadmapItem, deleteRoadmapItem, fetchEmployees, fetchVacations, getWsUrl, getWsToken } from "../api";
import DatePicker from "./DatePicker";
import CustomSelect from "./CustomSelect";
import WeekPicker from "./WeekPicker";
import ConfirmDialog from "./ConfirmDialog";
import { MONTH_NAMES } from "../constants";
import { addDays, getMonday, getISOWeek, getISOWeekYear, getMondayOfWeek, formatISODate, parseISODateLocal, isoWeekday, useToday } from "../lib/dates";
import "./Roadmap.css";
import "./FilterBar.css";

const COL_WIDTH_DESKTOP = 80;
const COL_WIDTH_MOBILE = 56;
const LABEL_WIDTH_DESKTOP = 140;
const LABEL_WIDTH_MOBILE = 70;

const ROADMAP_GROUPS = [
  { key: "_mitarbeiter", label: "Personal", color: "#6951be" },
  { key: "_events", label: "Events + Termine", color: "#caa45e" },
  { key: "_marketing", label: "Marketing + Vertrieb", color: "#469bdb" },
  { key: "_intern", label: "Intern", color: "#3870d8" },
  { key: "_extern", label: "Extern", color: "#30b3bd" },
];

export default function Roadmap() {
  const scrollRef = useRef(null);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vacations, setVacations] = useState([]);
  // First-paint gate — see Time.js: hold the view until the first load finishes.
  const [ready, setReady] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(null);
  const [formError, setFormError] = useState("");
  const [offset, setOffset] = useState(0);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [backlogGroup, setBacklogGroup] = useState("_extern");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterPersonIds, setFilterPersonIds] = useState([]);
  const filterRef = useRef(null);
  const dragRef = useRef(null);
  const didDragRef = useRef(false);
  // Removes the document-level drag listeners if the component unmounts while
  // a drag is still in progress (mouseup normally cleans them up itself).
  const dragCleanupRef = useRef(null);
  useEffect(() => () => { if (dragCleanupRef.current) dragCleanupRef.current(); }, []);
  const scrollPosRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const COL_WIDTH = isMobile ? COL_WIDTH_MOBILE : COL_WIDTH_DESKTOP;
  const LABEL_WIDTH = isMobile ? LABEL_WIDTH_MOBILE : LABEL_WIDTH_DESKTOP;

  // Reactive local date (rolls over after midnight / on tab focus). Memoizing on
  // the ISO string gives `currentMonday` a STABLE identity — the previous
  // `new Date()` per render made the weeks memo (and everything downstream of it)
  // recompute on every single render.
  const todayISO = useToday();
  const { currentMonday, currentWeek, currentYear } = useMemo(() => {
    const today = parseISODateLocal(todayISO);
    return {
      currentMonday: getMonday(today),
      currentWeek: getISOWeek(today),
      // ISO week-YEAR, not calendar year: around New Year they differ (KW 1 can
      // start in December, KW 52/53 can reach into January) and every (week, year)
      // pair in the roadmap uses the ISO convention.
      currentYear: getISOWeekYear(today),
    };
  }, [todayISO]);

  const WEEKS_BACK = 12;
  const WEEKS_FORWARD = 12;
  const TOTAL_WEEKS = WEEKS_BACK + WEEKS_FORWARD + 1;

  const weeks = useMemo(() => {
    const start = addDays(currentMonday, (-WEEKS_BACK + offset) * 7);
    return Array.from({ length: TOTAL_WEEKS }, (_, i) => {
      const monday = addDays(start, i * 7);
      return {
        monday,
        week: getISOWeek(monday),
        month: monday.getMonth(),
        // Calendar year of the Monday — for the month header. `year` is the ISO
        // week-year for (week, year) lookups; using it in the month label made a
        // 29.-31.12. Monday appear as an extra "DEZEMBER <Folgejahr>" span.
        monthYear: monday.getFullYear(),
        year: getISOWeekYear(monday),
        isCurrent: getISOWeek(monday) === currentWeek && getISOWeekYear(monday) === currentYear,
      };
    });
  }, [currentMonday, currentWeek, currentYear, offset]);

  const monthSpans = useMemo(() => {
    const spans = [];
    let current = null;
    weeks.forEach((w) => {
      const key = `${w.monthYear}-${w.month}`;
      if (!current || current.key !== key) {
        current = { key, label: `${MONTH_NAMES[w.month].toUpperCase()} ${w.monthYear}`, count: 1 };
        spans.push(current);
      } else {
        current.count++;
      }
    });
    return spans;
  }, [weeks]);

  const loadEmployees = useCallback(async () => {
    try {
      // Include archived so departed staff's past vacation still resolves to a name;
      // pickers below use `activeEmployees` so archived people aren't assignable.
      const data = await fetchEmployees(true);
      setEmployees(data);
    } catch (e) { /* ignore */ }
  }, []);

  // Active people only — for filter chips and assignment (archived stay resolvable
  // for name display but are not assignable/filterable).
  const activeEmployees = useMemo(() => (employees || []).filter((e) => !e.archived), [employees]);

  const loadItems = useCallback(async () => {
    try {
      const data = await fetchRoadmapItems();
      setItems(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadVacations = useCallback(async () => {
    try {
      // Admins see all employees' requests, others get their own (backend handles auth)
      const data = await fetchVacations();
      setVacations(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all([loadItems(), loadEmployees(), loadVacations()]);
  }, [loadItems, loadEmployees, loadVacations]);

  useEffect(() => { loadAll().finally(() => setReady(true)); }, [loadAll]);

  // WebSocket for real-time roadmap updates
  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(getWsUrl());
      ws.onopen = () => {
        const token = getWsToken();
        if (token) ws.send(JSON.stringify({ token }));
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "roadmap") loadItems();
          if (msg.type === "workforce") loadVacations();
        } catch {}
      };
      // disposed guard: the cleanup's ws.close() fires onclose asynchronously —
      // without it every unmount left a zombie socket reconnecting forever.
      ws.onclose = () => {
        if (!disposed) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        ws.close();
      };
    };
    let disposed = false;
    connect();
    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [loadItems, loadVacations]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!filterOpen) return;
    function handleClick(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [filterOpen]);

  // Scroll to current week on initial mount only. `ready` must be a dep: before
  // the first load the component early-returns without the scroll container, and
  // since `weeks` is identity-stable this effect would otherwise never re-run.
  const initialScrollDone = useRef(false);
  useEffect(() => {
    if (ready && scrollRef.current && !initialScrollDone.current) {
      const currentIdx = weeks.findIndex((w) => w.isCurrent);
      if (currentIdx >= 0) {
        scrollRef.current.scrollLeft = currentIdx * COL_WIDTH - scrollRef.current.clientWidth / 2 + LABEL_WIDTH;
        initialScrollDone.current = true;
      }
    }
  }, [weeks, ready, COL_WIDTH, LABEL_WIDTH]);

  // Restore scroll position after data reload
  useEffect(() => {
    if (scrollPosRef.current && scrollRef.current) {
      scrollRef.current.scrollLeft = scrollPosRef.current.left;
      scrollRef.current.scrollTop = scrollPosRef.current.top;
      scrollPosRef.current = null;
    }
  }, [items]);

  function saveScrollPos() {
    if (scrollRef.current) {
      scrollPosRef.current = {
        left: scrollRef.current.scrollLeft,
        top: scrollRef.current.scrollTop,
      };
    }
  }

  // True week index relative to the window's first week. Weeks are contiguous,
  // so plain date math replaces the old findIndex scan — and, unlike it, yields
  // real negative / beyond-window indices for weeks outside the visible range.
  function weekIndex(week, year) {
    return Math.round((getMondayOfWeek(year, week) - weeks[0].monday) / (7 * 86400000));
  }
  // Week descriptor at a (possibly off-window) index — inverse of weekIndex.
  function weekAt(idx) {
    if (idx >= 0 && idx < weeks.length) return weeks[idx];
    const monday = addDays(weeks[0].monday, idx * 7);
    return { monday, week: getISOWeek(monday), year: getISOWeekYear(monday) };
  }

  // Backlog items - highlighted first
  const backlogItems = useMemo(() => {
    const bl = items.filter((i) => i.backlog);
    bl.sort((a, b) => {
      const ah = a.backlog_highlight ? 0 : 1;
      const bh = b.backlog_highlight ? 0 : 1;
      if (ah !== bh) return ah - bh;
      return a.id - b.id;
    });
    return bl;
  }, [items]);
  const backlogByGroup = useMemo(() => {
    const map = {};
    ROADMAP_GROUPS.forEach((g) => { map[g.key] = []; });
    backlogItems.forEach((item) => {
      if (map[item.group_key]) map[item.group_key].push(item);
    });
    return map;
  }, [backlogItems]);

  // Render vacation requests (vacation_requests) as synthetic roadmap items
  const syntheticVacationItems = useMemo(() => {
    // Two requests that are only separated by a weekend describe one continuous
    // absence — merge them so the roadmap shows a single bar instead of two.
    // Cancelled requests no longer occupy the range, same as rejected ones.
    const byUser = new Map();
    for (const v of (vacations || []).filter((v) => v.status !== "rejected" && v.status !== "cancelled")) {
      if (!byUser.has(v.user_id)) byUser.set(v.user_id, []);
      byUser.get(v.user_id).push(v);
    }
    const blocks = [];
    for (const [userId, list] of byUser) {
      list.sort((a, b) => a.start_date.localeCompare(b.start_date));
      let cur = null;
      for (const v of list) {
        if (cur) {
          // Only weekend days between the previous end and this start? → same block.
          let gapOnlyWeekend = true;
          const probe = parseISODateLocal(cur.end_date);
          probe.setDate(probe.getDate() + 1);
          const next = parseISODateLocal(v.start_date);
          while (probe < next) {
            const wd = probe.getDay();
            if (wd !== 0 && wd !== 6) { gapOnlyWeekend = false; break; }
            probe.setDate(probe.getDate() + 1);
          }
          // Same status only: merging a pending extension into an approved block
          // (or vice versa) painted the whole bar with the FIRST block's status.
          if (gapOnlyWeekend && v.start_date > cur.end_date && v.status === cur.status) {
            cur = { ...cur, end_date: v.end_date, ids: [...cur.ids, v.id] };
            blocks[blocks.length - 1] = cur;
            continue;
          }
        }
        cur = { ...v, user_id: userId, ids: [v.id] };
        blocks.push(cur);
      }
    }
    return blocks.map((v) => {
      const startDate = parseISODateLocal(v.start_date);
      const endDate = parseISODateLocal(v.end_date);
      const employee = employees.find((e) => e.id === v.user_id);
      // Which half of its week each end sits in (1=Mon … 7=Sun): starting Wed or
      // later means the bar begins mid-week, ending Wed or earlier means it stops
      // mid-week. A short vacation therefore takes the correct half automatically.
      return {
        id: `vac-${v.ids.join("-")}`,
        group_key: "_mitarbeiter",
        title: "Urlaub",
        start_week: getISOWeek(startDate),
        start_year: getISOWeekYear(startDate),
        start_half: isoWeekday(startDate) >= 3,
        end_week: getISOWeek(endDate),
        end_year: getISOWeekYear(endDate),
        end_half: isoWeekday(endDate) <= 3,
        color: "#8a6fd4",
        date: v.start_date,
        date_until: v.end_date,
        completed: false,
        backlog: false,
        person_ids: [v.user_id],
        persons: employee ? [employee] : [],
        synthetic: true,
        status: v.status,
      };
    });
  }, [vacations, employees]);

  // Group items - sort by start date (earliest first), not alphabetically
  const itemsByGroup = useMemo(() => {
    const map = {};
    ROADMAP_GROUPS.forEach((g) => { map[g.key] = []; });
    // "Urlaub" now comes exclusively from the vacation requests — hide old manual items
    const realItems = items.filter((it) => it.title !== "Urlaub" && it.title !== "Krank");
    const allItems = [...realItems, ...syntheticVacationItems];
    allItems.forEach((item) => {
      if (!item.backlog && map[item.group_key]) {
        // Person filter: if filter active, only show items that have at least one matching person
        if (filterPersonIds.length > 0) {
          const itemPersons = item.person_ids || [];
          if (itemPersons.length > 0 && !filterPersonIds.some((pid) => itemPersons.includes(pid))) return;
        }
        map[item.group_key].push(item);
      }
    });
    // Sort each group: earlier start_year/start_week first
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        const yearDiff = a.start_year - b.start_year;
        if (yearDiff !== 0) return yearDiff;
        const kwDiff = a.start_week - b.start_week;
        if (kwDiff !== 0) return kwDiff;
        return String(a.id).localeCompare(String(b.id));
      });
    }
    return map;
  }, [items, syntheticVacationItems, filterPersonIds]);

  // Overlap layout
  function buildRows(groupItems) {
    const rows = [];
    const HALF = COL_WIDTH / 2;
    const windowWidth = TOTAL_WEEKS * COL_WIDTH;
    groupItems.forEach((item) => {
      // TRUE (unclamped) indices: items reaching out of the window keep their
      // real extent, so dragging can no longer chop off the invisible part, and
      // an item spanning the entire window still renders (the old
      // findIndex === -1 check dropped it entirely).
      const startIdx = weekIndex(item.start_week, item.start_year);
      const endIdx = weekIndex(item.end_week, item.end_year);
      if (endIdx < 0 || startIdx > TOTAL_WEEKS - 1) return; // fully outside
      const trueLeftPx = startIdx * COL_WIDTH + (item.start_half ? HALF : 0);
      // A single mid-week day (start_half AND end_half in the same week) would
      // collapse to zero width — keep at least half a column visible.
      const trueRightPx = Math.max((endIdx + 1) * COL_WIDTH - (item.end_half ? HALF : 0), trueLeftPx + HALF);
      const leftPx = Math.max(trueLeftPx, 0);
      const rightPx = Math.min(trueRightPx, windowWidth);
      const bar = {
        startIdx: Math.max(startIdx, 0),
        endIdx: Math.min(endIdx, TOTAL_WEEKS - 1),
        leftPx,
        widthPx: rightPx - leftPx,
        trueLeftPx,
        trueWidthPx: trueRightPx - trueLeftPx,
        item,
      };
      let placed = false;
      for (const row of rows) {
        if (!row.some((b) => !(bar.leftPx + bar.widthPx <= b.leftPx || bar.leftPx >= b.leftPx + b.widthPx))) {
          row.push(bar);
          placed = true;
          break;
        }
      }
      if (!placed) rows.push([bar]);
    });
    return rows;
  }


  // Form helpers
  const groupColor = (groupKey) => ROADMAP_GROUPS.find((g) => g.key === groupKey)?.color || "#5ba3d9";

  const openCreate = (groupKey, week, year, isBacklog = false) => {
    setEditItem(null);
    setFormError("");
    let date = "";
    let dateUntil = "";
    if (groupKey === "_mitarbeiter" || groupKey === "_events") {
      // lib getMondayOfWeek takes (year, week) — the old local helper took (week, year)
      const monday = getMondayOfWeek(year, week);
      const friday = new Date(monday);
      friday.setDate(friday.getDate() + 4);
      date = formatISODate(monday);
      dateUntil = formatISODate(friday);
    }
    setFormData({
      title: "",
      group_key: groupKey,
      start_week: week,
      start_year: year,
      start_half: false,
      end_week: week,
      end_year: year,
      end_half: false,
      color: groupColor(groupKey),
      deadline: "",
      date,
      date_until: dateUntil,
      person_ids: [],
      completed: false,
      backlog: isBacklog,
    });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormError("");
    setFormData({
      title: item.title,
      group_key: item.group_key,
      start_week: item.start_week,
      start_year: item.start_year,
      start_half: item.start_half || false,
      end_week: item.end_week,
      end_year: item.end_year,
      end_half: item.end_half || false,
      color: item.color,
      deadline: item.deadline || "",
      date: item.date || "",
      date_until: item.date_until || "",
      person_ids: item.person_ids || [],
      completed: item.completed || false,
      backlog: item.backlog || false,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    saveScrollPos();
    const payload = {
      ...formData,
      color: groupColor(formData.group_key),
      start_half: formData.start_half || false,
      end_half: formData.end_half || false,
      deadline: formData.deadline || null,
      date: formData.date || null,
      date_until: formData.date_until || null,
      person_ids: formData.person_ids || [],
      completed: formData.completed || false,
      backlog: formData.backlog || false,
    };
    try {
      setFormError("");
      if (editItem) {
        await updateRoadmapItem(editItem.id, { ...payload, version: editItem.version });
      } else {
        await createRoadmapItem(payload);
      }
      setShowForm(false);
      await loadAll();
    } catch (e) {
      console.error(e);
      setFormError(e.message || "Fehler beim Speichern");
    }
  };

  const handleDuplicate = () => {
    if (!editItem) return;
    setEditItem(null);
    setFormData((prev) => ({ ...prev }));
    setFormError("");
  };

  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = () => {
    if (!editItem) return;
    setConfirmDelete(true);
  };

  const confirmDeleteAction = async () => {
    if (!editItem) return;
    saveScrollPos();
    try {
      await deleteRoadmapItem(editItem.id);
      setShowForm(false);
      await loadAll();
    } catch (e) {
      console.error(e);
    }
    setConfirmDelete(false);
  };

  const handleToggleBacklogHighlight = async (item) => {
    try {
      await updateRoadmapItem(item.id, { backlog_highlight: !item.backlog_highlight, version: item.version });
      await loadAll();
    } catch (e) {
      console.error(e);
    }
  };



  /* Unified drag handler - supports resize (left/right) AND move */
  const handleDragStart = useCallback((e, itemId, edge, origLeftPx, origWidthPx) => {
    e.stopPropagation();
    e.preventDefault();
    const HALF = COL_WIDTH / 2;
    dragRef.current = { itemId, edge, startX: e.clientX, origLeftPx, origWidthPx };
    didDragRef.current = false;

    const handleMouseMove = (ev) => {
      if (!dragRef.current) return;
      const { itemId: dragItemId, startX, origLeftPx, origWidthPx, edge } = dragRef.current;
      const dx = ev.clientX - startX;
      const halfShift = Math.round(dx / HALF) * HALF;
      if (halfShift === 0) return;
      didDragRef.current = true;

      setItems((prev) => prev.map((item) => {
        if (item.id !== dragItemId) return item;
        // TRUE coordinates (may be negative / beyond the window for clipped
        // bars) — no window clamps here, or dragging a clipped bar would snap
        // its hidden part to the window edge and silently change the data.
        let newLeft = origLeftPx;
        let newRight = origLeftPx + origWidthPx;

        if (edge === "left") {
          newLeft = Math.min(origLeftPx + halfShift, newRight - HALF);
        } else if (edge === "right") {
          newRight = Math.max(newLeft + HALF, origLeftPx + origWidthPx + halfShift);
        } else {
          // move: shift both edges together
          newLeft = origLeftPx + halfShift;
          newRight = newLeft + origWidthPx;
        }

        // Positive modulo — the plain % of a negative offset would flip the
        // half-week detection for bars starting left of the window.
        const posMod = (v) => ((v % COL_WIDTH) + COL_WIDTH) % COL_WIDTH;
        const startWeekIdx = Math.floor(newLeft / COL_WIDTH);
        const startHalf = posMod(newLeft) >= HALF;
        const endWeekIdx = Math.ceil(newRight / COL_WIDTH) - 1;
        const endHalf = posMod(newRight) > 0 && posMod(newRight) <= HALF;

        const startWeek = weekAt(startWeekIdx);
        const endWeek = weekAt(endWeekIdx);

        return {
          ...item,
          start_week: startWeek.week,
          start_year: startWeek.year,
          start_half: startHalf,
          end_week: endWeek.week,
          end_year: endWeek.year,
          end_half: endHalf,
        };
      }));
    };

    const handleMouseUp = async () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      dragCleanupRef.current = null;
      if (!dragRef.current) return;
      dragRef.current = null;
      // A plain click (mousedown+mouseup without movement) must NOT save: the
      // no-op PUT still bumped the version server-side, so the edit modal the
      // click opens next was guaranteed to 409 on its first save.
      if (!didDragRef.current) return;
      saveScrollPos();
      setItems((prev) => {
        const updated = prev.find((i) => i.id === itemId);
        if (updated) {
          updateRoadmapItem(updated.id, {
            start_week: updated.start_week,
            start_year: updated.start_year,
            start_half: updated.start_half,
            end_week: updated.end_week,
            end_year: updated.end_year,
            end_half: updated.end_half,
            version: updated.version,
          }).then((saved) => {
            // Adopt the server's new version so a follow-up drag/edit can't 409.
            if (saved?.id) setItems((cur) => cur.map((i) => (i.id === saved.id ? { ...i, ...saved } : i)));
          }).catch((e) => {
            // e.g. 409 version conflict — reload so the bar snaps back to the
            // server state instead of silently keeping the rejected position.
            console.error(e);
            loadItems();
          });
        }
        return prev;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    dragCleanupRef.current = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      dragRef.current = null;
    };
    // COL_WIDTH must be a dep: after a breakpoint flip the old callback would
    // keep snapping to the previous column width. (`items` was never used;
    // saveScrollPos/weekAt touch only refs resp. depend on `weeks` themselves.)
  }, [weeks, COL_WIDTH, loadItems]);

  // Toggle completed
  const handleToggleCompleted = async (item) => {
    saveScrollPos();
    try {
      await updateRoadmapItem(item.id, { completed: !item.completed, version: item.version });
      await loadAll();
    } catch (e) {
      console.error(e);
    }
  };

  const totalWidth = LABEL_WIDTH + TOTAL_WEEKS * COL_WIDTH;
  const ROW_HEIGHT = isMobile ? 40 : 48;
  const ROW_HEIGHT_EMPLOYEE = isMobile ? 26 : 28;
  const ROW_HEIGHT_EVENTS = isMobile ? 56 : 68;

  // parseISODateLocal, NOT new Date(iso): the latter parses as UTC midnight and
  // shows the previous day in timezones west of UTC.
  const fmtDate = (d) => d ? parseISODateLocal(String(d).slice(0, 10)).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : null;

  /* Render a single bar */
  const renderBar = (bar, rowIdx, rowHeight, isEvents, isEmployeeGroup) => {
    const persons = bar.item.persons || [];
    const isEvent = bar.item.group_key === "_events";
    const isCompleted = bar.item.completed;
    const isSynthetic = !!bar.item.synthetic;
    const isPending = isSynthetic && bar.item.status === "requested";
    const color = isSynthetic ? (bar.item.color || groupColor(bar.item.group_key)) : groupColor(bar.item.group_key);

    let dateStr = null;
    if (isEvent) {
      const d1 = fmtDate(bar.item.date);
      const d2 = fmtDate(bar.item.date_until);
      if (d1 && d2 && d1 !== d2) dateStr = `${d1} – ${d2}`;
      else if (d1) dateStr = d1;
    } else if (!isEmployeeGroup) {
      dateStr = fmtDate(bar.item.deadline);
    }

    const hasMeta = !isEmployeeGroup && (persons.length > 0 || dateStr);

    return (
      <div
        key={bar.item.id}
        className={`roadmap-bar ${isEvents ? "roadmap-bar-events" : ""} ${isEmployeeGroup ? "roadmap-bar-employee" : ""} ${isCompleted ? "roadmap-bar-done" : ""} ${isSynthetic ? "roadmap-bar-synthetic" : ""} ${isPending ? "roadmap-bar-pending" : ""}`}
        style={{
          left: bar.leftPx + 2,
          width: bar.widthPx - 4,
          top: rowIdx * rowHeight + 4,
          borderColor: isCompleted ? "#4a4a4a" : color,
          background: isCompleted ? "#2a2a2a44" : color + "22",
          color: isCompleted ? "#6b6b6b" : color,
        }}
        title={isSynthetic
          ? `${persons.map((p) => (p.abbreviation || p.name.split(" ")[0])).join(", ") || "Urlaub"} – ${bar.item.title} (${bar.item.status})`
          : isEmployeeGroup && persons.length > 0
            ? `${persons.map((p) => (p.abbreviation || p.name.split(" ")[0])).join(", ")} - ${bar.item.title}`
            : bar.item.title}
        onClick={() => { if (!didDragRef.current && !isSynthetic) openEdit(bar.item); }}
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          if (isSynthetic) return;
          if (e.target.closest(".roadmap-bar-handle")) return;
          handleDragStart(e, bar.item.id, "move", bar.trueLeftPx, bar.trueWidthPx);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isSynthetic) return;
          handleToggleCompleted(bar.item);
        }}
      >
        {!isSynthetic && <div
          className="roadmap-bar-handle roadmap-bar-handle-left"
          onMouseDown={(e) => handleDragStart(e, bar.item.id, "left", bar.trueLeftPx, bar.trueWidthPx)}
        />}
        <div className="roadmap-bar-content">
          <span className={`roadmap-bar-text ${isEvents ? "roadmap-bar-text-wrap" : ""}`}>
            {isEmployeeGroup && persons.length > 0
              ? `${persons.map((p) => (p.abbreviation || p.name.split(" ")[0])).join(", ")} - ${bar.item.title}`
              : bar.item.title}
          </span>
          {isEvents && dateStr && (
            <span className="roadmap-bar-date">{dateStr}</span>
          )}
          {isEvents && persons.length > 0 && (
            <span className="roadmap-bar-persons">
              {persons.map((p) => (
                <span key={p.id} className="roadmap-bar-person" style={{ background: isCompleted ? "#555" : p.color }} title={p.name}>
                  {p.abbreviation}
                </span>
              ))}
            </span>
          )}
          {!isEvents && hasMeta && (
            <div className="roadmap-bar-meta">
              <span className="roadmap-bar-date">{dateStr}</span>
              <span className="roadmap-bar-persons">
                {persons.map((p) => (
                  <span key={p.id} className="roadmap-bar-person" style={{ background: isCompleted ? "#555" : p.color }} title={p.name}>
                    {p.abbreviation}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
        {!isSynthetic && <div
          className="roadmap-bar-handle roadmap-bar-handle-right"
          onMouseDown={(e) => handleDragStart(e, bar.item.id, "right", bar.trueLeftPx, bar.trueWidthPx)}
        />}
      </div>
    );
  };

  const handleBacklogDrop = async (e, groupKey, week, year) => {
    const itemId = e.dataTransfer.getData("backlog-item-id");
    if (!itemId) return;
    e.preventDefault();
    saveScrollPos();
    try {
      const item = items.find((i) => i.id === Number(itemId));
      await updateRoadmapItem(Number(itemId), {
        group_key: groupKey,
        start_week: week,
        start_year: year,
        end_week: week,
        end_year: year,
        backlog: false,
        version: item?.version,
      });
      await loadAll();
    } catch (err) {
      console.error(err);
    }
  };

  const renderGridLines = (groupKey) => weeks.map((w, i) => (
    <div
      key={i}
      className={`roadmap-grid-line ${w.isCurrent ? "roadmap-grid-current" : ""}`}
      style={{ left: i * COL_WIDTH, width: COL_WIDTH }}
      onClick={() => openCreate(groupKey, w.week, w.year)}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; e.currentTarget.classList.add("drag-over"); }}
      onDragLeave={(e) => { e.currentTarget.classList.remove("drag-over"); }}
      onDrop={(e) => { e.currentTarget.classList.remove("drag-over"); handleBacklogDrop(e, groupKey, w.week, w.year); }}
    />
  ));

  // Calculate group heights dynamically based on content, but fill screen
  const groupData = useMemo(() => {
    return ROADMAP_GROUPS.map((group) => {
      const groupItems = itemsByGroup[group.key] || [];
      const rows = buildRows(groupItems);
      const isEvents = group.key === "_events";
      const isEmployeeGroup = group.key === "_mitarbeiter";
      const rh = isEmployeeGroup ? ROW_HEIGHT_EMPLOYEE : isEvents ? ROW_HEIGHT_EVENTS : ROW_HEIGHT;
      const minRows = rows.length > 0 ? rows.length : 1;
      const contentHeight = minRows * rh + 8;
      return { group, groupItems, rows, isEvents, isEmployeeGroup, rh, contentHeight };
    });
  }, [itemsByGroup, weeks, isMobile]);


  if (!ready) {
    return (
      <div
        className="roadmap-wrap"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "var(--text-muted)" }}
      >
        Laden…
      </div>
    );
  }

  return (
    <div className="roadmap-wrap">
      <div className="roadmap-nav">
        <div className="roadmap-nav-inner">
          <button className="cal-nav-btn" onClick={() => setOffset((o) => o - 12)} title="12 Wochen zurück">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button className="cal-nav-btn" onClick={() => setOffset((o) => o - 4)} title="4 Wochen zurück">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="cal-nav-today" onClick={() => setOffset(0)}>Heute</span>
          <button className="cal-nav-btn" onClick={() => setOffset((o) => o + 4)} title="4 Wochen vor">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="cal-nav-btn" onClick={() => setOffset((o) => o + 12)} title="12 Wochen vor">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 18 19 12 13 6"/><polyline points="5 18 11 12 5 6"/></svg>
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <div className="filter-wrap" ref={filterRef}>
          <button
            className={`btn-filter-toggle ${filterPersonIds.length > 0 ? "has-filters" : ""}`}
            onClick={() => setFilterOpen((o) => !o)}
            title="Filter"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {filterPersonIds.length > 0 && <span className="filter-dot" />}
          </button>
          {filterOpen && (
            <>
            <div className="mobile-backdrop" onClick={() => setFilterOpen(false)} />
            <div className="filter-overlay">
              <div className="filter-bar">
                <div className="filter-header">
                  <span className="filter-title">Filter</span>
                  <button
                    className="filter-reset"
                    style={{ visibility: filterPersonIds.length > 0 ? "visible" : "hidden" }}
                    onClick={() => { setFilterPersonIds([]); setFilterOpen(false); }}
                  >
                    Zurücksetzen
                  </button>
                </div>
                <div className="filter-fields">
                  <div className="filter-item">
                    <label>Person</label>
                    <div className="roadmap-person-chips">
                      {activeEmployees.map((m) => {
                        const selected = filterPersonIds.includes(m.id);
                        return (
                          <button
                            key={m.id}
                            type="button"
                            className={`roadmap-person-chip ${selected ? "roadmap-person-chip-active" : ""}`}
                            style={selected ? { background: m.color, borderColor: m.color } : {}}
                            onClick={() => {
                              setFilterPersonIds((prev) =>
                                selected ? prev.filter((id) => id !== m.id) : [...prev, m.id]
                              );
                            }}
                          >
                            {m.abbreviation || m.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
      <div className="roadmap-body">
      <div className="roadmap" ref={scrollRef}>
        <div className="roadmap-header-sticky" style={{ minWidth: totalWidth }}>
          {/* Month header */}
          <div className="roadmap-month-row">
            <div className="roadmap-label-cell roadmap-header-label" />
            {monthSpans.map((m) => (
              <div key={m.key} className="roadmap-month-cell" style={{ width: m.count * COL_WIDTH }}>
                {m.label}
              </div>
            ))}
          </div>

          {/* KW header */}
          <div className="roadmap-week-row">
            <div className="roadmap-label-cell roadmap-header-label" />
            {weeks.map((w, i) => (
              <div
                key={i}
                className={`roadmap-week-cell ${w.isCurrent ? "roadmap-week-current" : ""}`}
                style={{ width: COL_WIDTH }}
              >
                KW{String(w.week).padStart(2, "0")}
              </div>
            ))}
          </div>
        </div>

        <div className="roadmap-groups-container" style={{ minWidth: totalWidth }}>
          {/* Groups */}
          {groupData.map(({ group, rows, isEvents, isEmployeeGroup, rh, contentHeight }) => {
            return (
              <div key={group.key} className="roadmap-group" style={{ flex: `${contentHeight} 0 ${contentHeight}px` }}>
                <div className="roadmap-group-inner">
                  <div className="roadmap-label-cell roadmap-group-label">
                    {group.label}
                  </div>
                  <div className="roadmap-lanes" style={{ width: TOTAL_WEEKS * COL_WIDTH, minHeight: contentHeight }}>
                    {renderGridLines(group.key)}
                    {rows.map((row, rowIdx) =>
                      row.map((bar) => renderBar(bar, rowIdx, rh, isEvents, isEmployeeGroup))
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Create/Edit Modal */}
        {showForm && formData && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="roadmap-form modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">{editItem ? "Eintrag bearbeiten" : "Neuer Eintrag"}</h2>
                <button className="modal-close-btn" onClick={() => setShowForm(false)} title="Schließen">&times;</button>
              </div>
              <div className="roadmap-form-group-badge" style={{ color: groupColor(formData.group_key) }}>
                {ROADMAP_GROUPS.find((g) => g.key === formData.group_key)?.label}
              </div>

              <label className="roadmap-form-label">
                Titel
                {formData.group_key === "_mitarbeiter" ? (
                  <div className="roadmap-form-title-with-presets">
                    <input
                      className="roadmap-form-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      autoFocus
                      placeholder="Freitext oder Vorlage wählen"
                    />
                    <div className="roadmap-form-presets">
                      {["Frei", "Semesterferien", "Betriebsferien"]
                        .filter((preset) => !(formData.group_key === "_mitarbeiter" && preset === "Frei"))
                        .map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`roadmap-form-preset ${formData.title === preset ? "roadmap-form-preset-active" : ""}`}
                          onClick={() => {
                            const updates = { title: preset };
                            if (preset === "Betriebsferien") {
                              updates.person_ids = activeEmployees.map((m) => m.id);
                            }
                            setFormData({ ...formData, ...updates });
                          }}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <input
                    className="roadmap-form-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    autoFocus
                  />
                )}
              </label>

              {!formData.backlog && (
                <>
                  {formData.group_key === "_events" || formData.group_key === "_mitarbeiter" ? (
                    <div className="roadmap-form-row">
                      <label className="roadmap-form-label">
                        Startdatum
                        <DatePicker value={formData.date} onChange={(v) => {
                          const d = v ? parseISODateLocal(v) : null;
                          const updates = { date: v };
                          if (d) {
                            updates.start_week = getISOWeek(d);
                            updates.start_year = getISOWeekYear(d);
                          }
                          setFormData({ ...formData, ...updates });
                        }} />
                      </label>
                      <label className="roadmap-form-label">
                        Enddatum
                        <DatePicker value={formData.date_until} onChange={(v) => {
                          const d = v ? parseISODateLocal(v) : null;
                          const updates = { date_until: v };
                          if (d) {
                            updates.end_week = getISOWeek(d);
                            updates.end_year = getISOWeekYear(d);
                          }
                          setFormData({ ...formData, ...updates });
                        }} />
                      </label>
                    </div>
                  ) : (
                    <>
                      <div className="roadmap-form-row">
                        <label className="roadmap-form-label">
                          Start
                          <WeekPicker
                            week={formData.start_week}
                            year={formData.start_year}
                            onChange={(week, year) => setFormData({ ...formData, start_week: week, start_year: year })}
                          />
                        </label>

                        <label className="roadmap-form-label">
                          Ende
                          <WeekPicker
                            week={formData.end_week}
                            year={formData.end_year}
                            onChange={(week, year) => setFormData({ ...formData, end_week: week, end_year: year })}
                          />
                        </label>
                      </div>

                    </>
                  )}
                </>
              )}

              {formData.group_key !== "_mitarbeiter" && formData.group_key !== "_events" && (
                <label className="roadmap-form-label">
                  Deadline
                  <DatePicker value={formData.deadline} onChange={(v) => setFormData({ ...formData, deadline: v })} />
                </label>
              )}

              <div className="roadmap-form-label">
                Personen
                <div className="roadmap-person-chips">
                  {activeEmployees.map((m) => {
                    const selected = (formData.person_ids || []).includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className={`roadmap-person-chip ${selected ? "roadmap-person-chip-active" : ""}`}
                        style={selected ? { background: m.color, borderColor: m.color } : {}}
                        onClick={() => {
                          const ids = formData.person_ids || [];
                          setFormData({
                            ...formData,
                            person_ids: selected ? ids.filter((id) => id !== m.id) : [...ids, m.id],
                          });
                        }}
                      >
                        {m.abbreviation || m.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {formError && <div style={{ color: "#f87171", fontSize: 13, marginBottom: 8 }}>{formError}</div>}
              <div className="roadmap-form-actions">
                {editItem && (
                  <button className="btn btn-danger btn-sm" onClick={handleDelete}>Löschen</button>
                )}
                <div style={{ flex: 1 }} />
                {editItem && (
                  <button className="btn btn-secondary btn-sm" onClick={handleDuplicate}>Duplizieren</button>
                )}
                <div style={{ flex: 1 }} />
                <button className="btn btn-primary" onClick={handleSave}>{editItem ? "Speichern" : "Erstellen"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Backlog Panel */}
      <div className={`roadmap-backlog ${backlogOpen ? "roadmap-backlog-open" : ""}`}>
        <button className="roadmap-backlog-toggle" onClick={() => setBacklogOpen((o) => !o)}>
          Backlog
        </button>
        {backlogOpen && (
          <div className="roadmap-backlog-content">
            <div className="roadmap-backlog-add">
              <CustomSelect
                value={backlogGroup}
                options={ROADMAP_GROUPS.filter((g) => g.key !== "_mitarbeiter" && g.key !== "_events").map((g) => ({ value: g.key, label: g.label.toUpperCase() }))}
                onChange={setBacklogGroup}
              />
            </div>
            {(() => {
              const g = ROADMAP_GROUPS.find((gr) => gr.key === backlogGroup);
              const groupItems = backlogByGroup[backlogGroup] || [];
              const color = g ? g.color : "#5ba3d9";
              return groupItems.map((item) => (
                <div
                  key={item.id}
                  className="roadmap-backlog-item"
                  draggable
                  style={{
                    borderColor: color,
                    background: item.backlog_highlight ? color + "22" : "transparent",
                    color: item.backlog_highlight ? color : "#71717a",
                  }}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("backlog-item-id", String(item.id));
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onClick={() => openEdit(item)}
                  onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleBacklogHighlight(item); }}
                  title="Rechtsklick: Wichtigkeit anpassen"
                >
                  <span className="roadmap-backlog-item-title">{item.title}</span>
                </div>
              ));
            })()}
            <div
              className="roadmap-backlog-empty"
              onClick={() => openCreate(backlogGroup, currentWeek, currentYear, true)}
            />
          </div>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog
          message={`"${editItem?.title}" wirklich löschen?`}
          onConfirm={confirmDeleteAction}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
